import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Calendar, Clock, Music, Droplet, BookOpen, Heart } from 'lucide-react';
import { classes, businessInfo } from '../data/mock';

const ClassesPage = () => {
  const getIcon = (iconName) => {
    const icons = {
      stretch: Heart,
      music: Music,
      droplet: Droplet,
      book: BookOpen
    };
    return icons[iconName] || Heart;
  };

  return (
    <div>
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-[#AF9EB8]/10 via-white to-[#F7F8FA] overflow-hidden">
        <div className="container-custom relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="inline-flex items-center gap-2 px-4 py-2 bg-[#AF9EB8]/10 rounded-full mb-6">
              <Calendar size={16} className="text-[#AF9EB8]" />
              <span className="text-sm font-medium text-[#AF9EB8]">Community Events</span>
            </span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-serif font-medium text-[#2C2F36] mb-6">
              Classes & Events
            </h1>
            <p className="text-xl text-[#4A5568] mb-8 max-w-3xl mx-auto">
              Join our community classes and events designed to support your overall wellness, 
              from yoga and Zumba to essential oil workshops.
            </p>
          </div>
        </div>
      </section>

      {/* Free Orientation Banner */}
      <section className="bg-[#6E9EB9] py-8">
        <div className="container-custom">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6 text-white">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-white/20 rounded-full">
                <Calendar size={28} />
              </div>
              <div>
                <h3 className="text-xl font-serif font-medium">Free Orientation Classes</h3>
                <p className="text-white/90">
                  1st & 3rd Tuesday of each month at 6:30 PM
                </p>
              </div>
            </div>
            <Link 
              to="/orientation" 
              className="px-6 py-3 bg-white text-[#6E9EB9] rounded-full font-semibold hover:bg-white/90 transition-colors flex items-center gap-2"
            >
              Learn More
              <ArrowRight size={18} />
            </Link>
          </div>
        </div>
      </section>

      {/* Classes Grid */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#6E9EB9]/10 rounded-full text-sm font-medium text-[#6E9EB9] mb-4">
              Wellness Classes
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              Our Regular Classes
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              We offer a variety of classes to support your physical, mental, and emotional wellness.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {classes.map((classItem) => {
              const IconComponent = getIcon(classItem.icon);
              return (
                <div 
                  key={classItem.id}
                  className="bg-[#F7F8FA] rounded-2xl p-8 hover:shadow-md transition-shadow"
                >
                  <div className="flex items-start gap-4">
                    <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                      <IconComponent size={28} className="text-[#6E9EB9]" />
                    </div>
                    <div className="flex-1">
                      <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-2">
                        {classItem.name}
                      </h3>
                      <p className="text-[#4A5568] mb-4">
                        {classItem.description}
                      </p>
                      <div className="flex items-center gap-2 text-sm text-[#6E9EB9]">
                        <Clock size={16} />
                        <span>{classItem.schedule}</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Essential Oil Programs */}
      <section className="section-padding bg-gradient-to-br from-[#AF9EB8]/10 to-white">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="inline-block px-4 py-1 bg-[#AF9EB8]/10 rounded-full text-sm font-medium text-[#AF9EB8] mb-4">
                After Hours
              </span>
              <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-6">
                Essential Oil Programs
              </h2>
              <p className="text-[#4A5568] mb-6 leading-relaxed">
                Join our after-hours essential oil programs where you'll learn about the benefits 
                and uses of high-quality essential oils for health and wellness.
              </p>
              <ul className="space-y-4 mb-8">
                {[
                  "Introduction to essential oils and aromatherapy",
                  "Safe usage and application methods",
                  "Creating your own blends",
                  "Essential oils for common health concerns",
                  "Building a natural wellness toolkit"
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <Droplet size={18} className="text-[#AF9EB8] mt-0.5 flex-shrink-0" />
                    <span className="text-[#4A5568]">{item}</span>
                  </li>
                ))}
              </ul>
              <Link to="/contact" className="btn-pink">
                Inquire About Essential Oil Classes
                <ArrowRight size={18} />
              </Link>
            </div>
            <div className="bg-white rounded-2xl p-8 shadow-sm">
              <h3 className="font-serif text-xl font-medium text-[#2C2F36] mb-6">
                Interested in Attending?
              </h3>
              <p className="text-[#4A5568] mb-6">
                Contact us to learn about upcoming essential oil programs and other 
                after-hours events. We'll be happy to add you to our notification list.
              </p>
              <div className="space-y-4">
                <a 
                  href={`mailto:${businessInfo.email}?subject=Essential Oil Program Inquiry`}
                  className="block p-4 rounded-xl bg-[#AF9EB8]/5 hover:bg-[#AF9EB8]/10 transition-colors text-center"
                >
                  <p className="font-medium text-[#2C2F36]">Email Us</p>
                  <p className="text-[#AF9EB8]">{businessInfo.email}</p>
                </a>
                <a 
                  href={`tel:${businessInfo.phone}`}
                  className="block p-4 rounded-xl bg-[#6E9EB9]/5 hover:bg-[#6E9EB9]/10 transition-colors text-center"
                >
                  <p className="font-medium text-[#2C2F36]">Call Us</p>
                  <p className="text-[#6E9EB9]">{businessInfo.phone}</p>
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Movement Classes */}
      <section className="section-padding bg-white">
        <div className="container-custom">
          <div className="text-center mb-12">
            <span className="inline-block px-4 py-1 bg-[#93A8C7]/10 rounded-full text-sm font-medium text-[#93A8C7] mb-4">
              Movement & Fitness
            </span>
            <h2 className="text-3xl md:text-4xl font-serif font-medium text-[#2C2F36] mb-4">
              Move Your Body
            </h2>
            <div className="floral-divider w-24 mx-auto mb-6" />
            <p className="text-[#4A5568] max-w-2xl mx-auto">
              Physical movement is an important part of overall wellness. Our yoga and Zumba 
              classes are designed for all fitness levels.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-[#6E9EB9]/5 to-white border border-[#6E9EB9]/10">
              <div className="w-16 h-16 bg-[#6E9EB9]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Heart size={32} className="text-[#6E9EB9]" />
              </div>
              <h3 className="font-serif text-2xl font-medium text-[#2C2F36] mb-4">Yoga</h3>
              <p className="text-[#4A5568] mb-6">
                Gentle yoga sessions perfect for all levels. Focus on flexibility, 
                relaxation, and mind-body connection.
              </p>
              <p className="text-sm text-[#6E9EB9] font-medium">Check schedule for class times</p>
            </div>
            <div className="text-center p-8 rounded-2xl bg-gradient-to-b from-[#AF9EB8]/5 to-white border border-[#AF9EB8]/10">
              <div className="w-16 h-16 bg-[#AF9EB8]/10 rounded-full flex items-center justify-center mx-auto mb-6">
                <Music size={32} className="text-[#AF9EB8]" />
              </div>
              <h3 className="font-serif text-2xl font-medium text-[#2C2F36] mb-4">Zumba</h3>
              <p className="text-[#4A5568] mb-6">
                Fun, energetic dance fitness classes that make exercise enjoyable 
                while improving cardiovascular health.
              </p>
              <p className="text-sm text-[#AF9EB8] font-medium">Check schedule for class times</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-[#AF9EB8] to-[#A08CA8] text-white">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl font-serif font-medium mb-6">
            Want to Stay Updated on Classes & Events?
          </h2>
          <p className="text-white/90 max-w-2xl mx-auto mb-8">
            Contact us to get on our mailing list and be the first to know about 
            upcoming classes, workshops, and special events.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to="/contact" 
              className="px-8 py-4 bg-white text-[#AF9EB8] rounded-full font-semibold hover:bg-white/90 transition-colors inline-flex items-center justify-center gap-2"
            >
              Contact Us
              <ArrowRight size={18} />
            </Link>
            <Link 
              to="/orientation" 
              className="px-8 py-4 bg-transparent border-2 border-white text-white rounded-full font-semibold hover:bg-white/10 transition-colors inline-flex items-center justify-center gap-2"
            >
              Free Orientation Class
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ClassesPage;